import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import { itineraryApi } from '../services/api';
import { clearImageCache, fetchCityWikiImage, fetchMultipleWikiImages } from '../components/LocationImage';
import {
  BudgetBanner, GalleryModal, DeleteDialog, ShareDialog, InviteDialog,
  getDestinationImages, HeroSection, TripActionBar, DiscoverGallery,
  TripDetailsCards, ItineraryTabsSection, GenerationState,
} from '../components/itinerary';
import { SocialShareDialog } from '../components/SocialShareDialog';
import { BudgetSocialShareDialog } from '../components/BudgetSocialShareDialog';
import { PublishTemplateDialog } from '../components/PublishTemplateDialog';
import { TripReviewSection } from '../components/itinerary/TripReviewSection';
import { useCurrencyConversion } from '../hooks/useCurrencyConversion';
import { downloadItineraryPdf } from '../utils/downloadItineraryPdf';
import { Loader2 } from 'lucide-react';

const ItineraryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'schedule';

  // Core state
  const [activeTab, setActiveTab] = useState(initialTab);
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [generatingProgress, setGeneratingProgress] = useState('');
  const [generationBgImage, setGenerationBgImage] = useState(null);

  // Data state
  const [weatherData, setWeatherData] = useState(null);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [insuranceData, setInsuranceData] = useState(null);
  const [loadingInsurance, setLoadingInsurance] = useState(false);
  const [visaData, setVisaData] = useState(null);
  const [loadingVisa, setLoadingVisa] = useState(false);
  const [companions, setCompanions] = useState([]);
  const [dynamicDestImages, setDynamicDestImages] = useState([]);
  const [destImagesLoading, setDestImagesLoading] = useState(true);

  // Dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmails, setShareEmails] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [showShareBudgetDialog, setShowShareBudgetDialog] = useState(false);
  const [showSocialShare, setShowSocialShare] = useState(false);
  const [emailShareReturnTo, setEmailShareReturnTo] = useState(null);
  const [showPublishTemplate, setShowPublishTemplate] = useState(false);

  // Gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryLocation, setGalleryLocation] = useState('');

  // Derived data
  const trip_input = itinerary?.trip_input;
  const generatedItinerary = itinerary?.itinerary;
  const status = itinerary?.status;

  // Currency conversion hook
  const currencyProps = useCurrencyConversion(trip_input, generatedItinerary);
  const destinationImages = trip_input ? getDestinationImages(trip_input.destination_city) : [];

  // ─── Data Fetching ───
  const fetchItinerary = useCallback(async () => {
    try {
      clearImageCache();
      const response = await itineraryApi.get(id);
      setItinerary(response.data);
      setError(null);
    } catch {
      toast.error('Failed to load itinerary');
      navigate('/dashboard');
    } finally { setLoading(false); }
    // clearImageCache, itineraryApi, setItinerary, setError, setLoading, toast are stable refs
  }, [id, navigate]);

  useEffect(() => { fetchItinerary(); }, [fetchItinerary]);

  // Scroll to top on page load/refresh — disable browser scroll restoration
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    // Also scroll after a short delay to override any browser restoration
    const timer = setTimeout(() => window.scrollTo(0, 0), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!itinerary?.trip_input?.destination_city) return;
    const cityName = itinerary.trip_input.destination_city;
    setDestImagesLoading(true);
    fetchMultipleWikiImages(cityName + ' landmarks attractions', 5)
      .then(images => { if (images.length > 0) setDynamicDestImages(images.map(img => img.url)); })
      .finally(() => setDestImagesLoading(false));
  }, [itinerary?.trip_input?.destination_city]);

  const fetchWeather = async (city, country) => {
    if (weatherData) return;
    setLoadingWeather(true);
    try {
      const res = await itineraryApi.getWeatherForecast(city, country, trip_input?.start_date, trip_input?.end_date);
      if (res.data.success) setWeatherData(res.data.weather);
    } catch { /* silent */ }
    finally { setLoadingWeather(false); }
  };

  const fetchInsurance = async (destination, duration, travelers) => {
    if (insuranceData) return;
    setLoadingInsurance(true);
    try {
      const res = await itineraryApi.getTravelInsurance(destination, duration, travelers);
      if (res.data.success) setInsuranceData(res.data.insurance);
    } catch { /* silent */ }
    finally { setLoadingInsurance(false); }
  };

  const fetchCompanions = async () => {
    try {
      const res = await itineraryApi.listCompanions(id);
      setCompanions(res.data);
    } catch { /* silent */ }
  };

  const fetchVisaInfo = async (nationality, destination) => {
    if (visaData) return;
    setLoadingVisa(true);
    try {
      const res = await itineraryApi.getVisaInfo(nationality || 'Unknown', destination);
      if (res.data.success) setVisaData(res.data.visa_info);
    } catch { /* silent */ }
    finally { setLoadingVisa(false); }
  };

  // ─── Action Handlers ───
  const handleGenerate = async () => {
    if (!trip_input) return;
    setGenerating(true);
    setError(null);
    setGeneratingProgress('Connecting to AI service...');
    const cityName = trip_input.destination_city || trip_input.destination_country;
    if (cityName) fetchCityWikiImage(cityName).then(url => { if (url) setGenerationBgImage(url); });
    try {
      setGeneratingProgress('Generating your personalized itinerary...');
      const response = await itineraryApi.generateDirect(trip_input);
      if (response.data.success && response.data.itinerary) {
        setGeneratingProgress('Saving itinerary...');
        const saveResponse = await itineraryApi.save(id, response.data.itinerary);
        setItinerary(saveResponse.data);
        toast.success('Itinerary generated and saved successfully!');
      } else {
        const errorMsg = response.data.error || 'Failed to generate itinerary';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } catch (err) {
      const message = err.response?.data?.detail || err.message || 'Failed to generate itinerary';
      setError(message);
      toast.error(message);
    } finally {
      setGenerating(false);
      setGeneratingProgress('');
    }
  };

  const handleModifyPlan = () => {
    if (trip_input) sessionStorage.setItem('modifyTrip', JSON.stringify({ id: itinerary.id, trip_input }));
    navigate('/new-trip?modify=' + id);
  };

  const handleEditTrip = () => {
    if (trip_input) sessionStorage.setItem('editTrip', JSON.stringify({ id: itinerary.id, trip_input }));
    navigate('/new-trip');
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await itineraryApi.delete(id);
      toast.success('Itinerary deleted successfully');
      navigate('/dashboard');
    } catch { toast.error('Failed to delete itinerary'); }
    finally { setDeleting(false); setShowDeleteDialog(false); }
  };

  const openGallery = async (locationName) => {
    setGalleryLocation(locationName);
    setGalleryOpen(true);
    setGalleryLoading(true);
    setGalleryImages([]);
    try { setGalleryImages(await fetchMultipleWikiImages(locationName, 6)); }
    catch { setGalleryImages([]); }
    finally { setGalleryLoading(false); }
  };

  const handleInviteCompanion = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    try {
      await itineraryApi.inviteCompanion(id, { email: inviteEmail, name: inviteName || undefined });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail(''); setInviteName(''); setShowInviteDialog(false);
      fetchCompanions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to invite companion'); }
    finally { setInviting(false); }
  };

  const handleRemoveCompanion = async (companionId) => {
    try { await itineraryApi.removeCompanion(companionId); toast.success('Companion removed'); fetchCompanions(); }
    catch { toast.error('Failed to remove companion'); }
  };

  const handleSendEmail = async () => {
    if (!shareEmails.trim()) { toast.error('Please enter at least one email address'); return; }
    setSendingEmail(true);
    try {
      const emailList = shareEmails.split(',').map(e => e.trim()).filter(Boolean);
      const invalidEmails = emailList.filter(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
      if (invalidEmails.length > 0) { toast.error(`Invalid email(s): ${invalidEmails.join(', ')}`); setSendingEmail(false); return; }
      const response = await itineraryApi.shareEmail(id, emailList, shareMessage);
      if (response.data.success) {
        toast.success(`Itinerary sent to ${response.data.sent_count} recipient(s)!`);
        setShowShareDialog(false); setShareEmails(''); setShareMessage('');
      } else { toast.error('Failed to send emails. Please try again.'); }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send emails';
      if (errorMsg.includes('not configured')) {
        try { await navigator.clipboard.writeText(window.location.href); toast.info('Email service unavailable. Link copied to clipboard instead!'); }
        catch { toast.error('Email service not configured. Please copy the link manually.'); }
      } else { toast.error(errorMsg); }
    } finally { setSendingEmail(false); }
  };

  const handleBookFlight = () => {
    try { window.open('https://travelean.com', '_blank', 'noopener,noreferrer'); toast.success('Opening Travelean in new tab...'); }
    catch { toast.error('Unable to open booking site. Please try again.'); }
  };

  const handleBookLodging = () => {
    try {
      const checkIn = new Date(trip_input.start_date).toISOString().split('T')[0];
      const checkOut = new Date(trip_input.end_date).toISOString().split('T')[0];
      const url = `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(trip_input.destination_city)}&checkin=${checkIn}&checkout=${checkOut}&group_adults=${trip_input.adults}&no_rooms=1&group_children=${trip_input.kids || 0}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success('Opening Booking.com in new tab...');
    } catch { toast.error('Unable to open booking site. Please try again.'); }
  };

  // ─── Loading State ───
  if (loading) {
    return (
      <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
        <div className="fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-ocean via-sky to-ocean-light" />
          <div className="absolute top-20 left-10 w-96 h-96 bg-gold/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-40 right-20 w-80 h-80 bg-white/20 rounded-full blur-3xl animate-pulse" />
        </div>
        <div className="bg-white/90 backdrop-blur-xl rounded-2xl p-8 shadow-2xl">
          <Loader2 className="w-8 h-8 animate-spin text-ocean" />
        </div>
      </div>
    );
  }

  if (!itinerary) return null;

  // ─── Render ───
  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="itinerary-page">
      {/* Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/80 to-sky/60" />
        <div className="absolute top-40 left-10 w-80 h-80 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-sky/25 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1.5s'}} />
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-white/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '3s'}} />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
      </div>

      <Header />

      <HeroSection
        tripInput={trip_input}
        dynamicDestImages={dynamicDestImages}
        destinationImages={destinationImages}
        onBack={() => navigate('/dashboard')}
      />

      <main className="px-6 md:px-12 lg:px-24 py-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          <TripActionBar
            tripInput={trip_input}
            status={status}
            generating={generating}
            itineraryId={id}
            onModifyPlan={handleModifyPlan}
            onEditTrip={handleEditTrip}
            onGenerate={handleGenerate}
            onShare={() => setShowSocialShare(true)}
            onDownload={() => downloadItineraryPdf(trip_input, generatedItinerary)}
            onDelete={() => setShowDeleteDialog(true)}
            onPublish={() => setShowPublishTemplate(true)}
          />

          <DiscoverGallery
            tripInput={trip_input}
            dynamicDestImages={dynamicDestImages}
            destinationImages={destinationImages}
            destImagesLoading={destImagesLoading}
          />

          <TripDetailsCards tripInput={trip_input} status={status} />

          {status === 'completed' && generatedItinerary ? (
            <>
              <BudgetBanner
                generatedItinerary={generatedItinerary}
                {...currencyProps}
                setShowShareBudgetDialog={setShowShareBudgetDialog}
              />

              <ItineraryTabsSection
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                itineraryId={id}
                generatedItinerary={generatedItinerary}
                tripInput={trip_input}
                currencyProps={currencyProps}
                visaData={visaData}
                loadingVisa={loadingVisa}
                weatherData={weatherData}
                loadingWeather={loadingWeather}
                insuranceData={insuranceData}
                loadingInsurance={loadingInsurance}
                companions={companions}
                fetchVisaInfo={fetchVisaInfo}
                fetchWeather={fetchWeather}
                fetchInsurance={fetchInsurance}
                fetchCompanions={fetchCompanions}
                openGallery={openGallery}
                handleBookFlight={handleBookFlight}
                handleBookLodging={handleBookLodging}
                setShowInviteDialog={setShowInviteDialog}
                handleRemoveCompanion={handleRemoveCompanion}
                onItineraryUpdate={(updatedItinerary) => {
                  setItinerary(prev => prev ? { ...prev, itinerary: updatedItinerary } : prev);
                }}
              />

              <div className="mt-6">
                <TripReviewSection
                  itineraryId={id}
                  destinationCity={trip_input?.destination_city || ''}
                  destinationCountry={trip_input?.destination_country || ''}
                />
              </div>
            </>
          ) : (
            <GenerationState
              generating={generating}
              generatingProgress={generatingProgress}
              error={error}
              generationBgImage={generationBgImage}
              tripInput={trip_input}
              onModifyPlan={handleModifyPlan}
              onGenerate={handleGenerate}
            />
          )}
        </div>
      </main>

      {/* Dialogs */}
      <DeleteDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog} onDelete={handleDelete} deleting={deleting} trip_input={trip_input} />
      <ShareDialog open={showShareDialog} onOpenChange={(open) => { setShowShareDialog(open); if (!open && emailShareReturnTo) { if (emailShareReturnTo === 'trip') setShowSocialShare(true); else if (emailShareReturnTo === 'budget') setShowShareBudgetDialog(true); setEmailShareReturnTo(null); } }} shareEmails={shareEmails} setShareEmails={setShareEmails} shareMessage={shareMessage} setShareMessage={setShareMessage} onSendEmail={handleSendEmail} sendingEmail={sendingEmail} trip_input={trip_input} />
      <InviteDialog open={showInviteDialog} onOpenChange={setShowInviteDialog} inviteEmail={inviteEmail} setInviteEmail={setInviteEmail} inviteName={inviteName} setInviteName={setInviteName} onInvite={handleInviteCompanion} inviting={inviting} />
      <BudgetSocialShareDialog open={showShareBudgetDialog} onOpenChange={setShowShareBudgetDialog} itineraryId={id} tripInput={trip_input} generatedItinerary={generatedItinerary} currencyMode={currencyProps.currencyMode} convertedBudget={currencyProps.convertedBudget} onEmailShare={() => { setEmailShareReturnTo('budget'); setShowShareDialog(true); }} />
      <GalleryModal galleryOpen={galleryOpen} setGalleryOpen={setGalleryOpen} galleryImages={galleryImages} galleryLoading={galleryLoading} galleryLocation={galleryLocation} />
      <SocialShareDialog open={showSocialShare} onOpenChange={setShowSocialShare} itineraryId={id} tripInput={trip_input} generatedItinerary={generatedItinerary} onEmailShare={() => { setEmailShareReturnTo('trip'); setShowShareDialog(true); }} />
      <PublishTemplateDialog open={showPublishTemplate} onOpenChange={setShowPublishTemplate} itineraryId={id} tripInput={trip_input} generatedItinerary={generatedItinerary} />
    </div>
  );
};

export default ItineraryPage;
